import { Link } from "react-router-dom";

/**
 * iter56 — Editorial page hero.
 *
 * Renders a full-width magazine-style page header used across Dashboard,
 * Bookings, Team, and Post-Production. Composition:
 *
 *   [eyebrow · small-caps]              [right-slot actions]
 *   Bold serif headline (Playfair)
 *   Muted subtitle sentence
 *   ─── hairline divider ─────────────
 *
 * `right` may be any node (buttons, filters, month picker). Keep it
 * lightweight — the hero is a header, not a toolbar.
 */
export default function PageHero({ eyebrow, title, subtitle, right, dense = false }) {
  return (
    <header className={`fade-in ${dense ? "pb-4" : "pb-6"}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 flex-1">
          {eyebrow && <div className="editorial-eyebrow mb-2">{eyebrow}</div>}
          <h1 className={dense ? "editorial-h2" : "editorial-h1"} data-testid="page-hero-title">
            {title}
          </h1>
          {subtitle && (
            <p className="editorial-sub mt-2" data-testid="page-hero-subtitle">
              {subtitle}
            </p>
          )}
        </div>
        {right && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">{right}</div>
        )}
      </div>
      <hr className="hairline mt-6" />
    </header>
  );
}

/** Optional breadcrumb rail — reserved for later nested pages. */
export function PageBreadcrumb({ items = [] }) {
  if (!items.length) return null;
  return (
    <nav className="text-xs text-stone-500 mb-3 flex items-center gap-1.5">
      {items.map((it, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-stone-300">›</span>}
          {it.to ? (
            <Link to={it.to} className="hover:text-rose-700">{it.label}</Link>
          ) : (
            <span className="text-stone-700 font-semibold">{it.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
